import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BookOpenCheck, ListTree } from "lucide-react";
import { defineModuleUi, rpc, useOrg } from "@vixio/data";
import { PageHeader, Tabs, formatMoney, monthStartIso, todayIso, lazyPage } from "@vixio/ui";
import "./api";
import { usePeriods } from "./api";

export * from "./api";

// Telas carregadas sob demanda: cada uma vira um arquivo à parte no build.
const LedgerTab = lazyPage(() => import("./LedgerTab"), "LedgerTab");
const DreTab = lazyPage(() => import("./ReportsTabs"), "DreTab");
const MarginTab = lazyPage(() => import("./ReportsTabs"), "MarginTab");
const TrialTab = lazyPage(() => import("./ReportsTabs"), "TrialTab");
const PeriodsTab = lazyPage(() => import("./PeriodsTab"), "PeriodsTab");
const PlanPage = lazyPage(() => import("./PlanPage"), "PlanPage");

function AccountingPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("aba") ?? "lancamentos";
  return (
    <div className="vx-content">
      <PageHeader
        title="Contábil"
        description="Partidas dobradas imutáveis, lançamentos automáticos por evento, balancete, DRE e margem por centro de custo."
        actions={<Link className="vx-btn" to="/dinheiro/plano-de-contas">Plano de contas e regras</Link>}
      />
      <Tabs
        label="Contábil"
        value={tab}
        onValueChange={(v) => setParams({ aba: v }, { replace: true })}
        tabs={[
          { value: "lancamentos", label: "Lançamentos", content: <LedgerTab /> },
          { value: "balancete", label: "Balancete", content: <TrialTab /> },
          { value: "dre", label: "DRE contábil", content: <DreTab /> },
          { value: "margem", label: "Margem por centro", content: <MarginTab /> },
          { value: "periodos", label: "Períodos", content: <PeriodsTab /> },
        ]}
      />
    </div>
  );
}

function AccountingWidget() {
  const org = useOrg();
  const today = todayIso();
  const from = monthStartIso(today);
  const dre = useQuery({
    queryKey: ["accounting", "dre", org.id, from, today],
    queryFn: async () => (await rpc("accounting", "dre", { p_org: org.id, p_from: from, p_to: today })) ?? [],
  });
  const periods = usePeriods(org.id).data ?? [];
  const get = (line: string) => Number(dre.data?.find((x) => x.line === line)?.amount_cents ?? 0);
  const open = periods.filter((p) => p.status === "open").length;
  const net = get("resultado_liquido");
  return (
    <dl className="vx-dl">
      <dt>Receita no mês</dt>
      <dd className="num">{formatMoney(get("receita_bruta"))}</dd>
      <dt>Resultado no mês</dt>
      <dd className={net < 0 ? "num text-crit" : "num"}>{formatMoney(net)}</dd>
      <dt>Períodos abertos</dt>
      <dd className="num">{open}</dd>
      <dt />
      <dd>
        <Link to="/dinheiro/contabil?aba=dre">Abrir DRE contábil</Link>
      </dd>
    </dl>
  );
}

export default defineModuleUi({
  key: "accounting",
  title: "Contábil gerencial",
  domain: "money",
  icon: BookOpenCheck,
  menu: [
    { path: "/dinheiro/contabil", label: "Contábil", minRole: "manager", icon: BookOpenCheck, order: 85, keywords: "lançamentos razão balancete dre partidas dobradas estorno período fechamento margem centro de custo" },
    { path: "/dinheiro/plano-de-contas", label: "Plano de contas", minRole: "manager", icon: ListTree, order: 86, keywords: "contas centros de custo regras por evento contabilização" },
  ],
  routes: [
    { path: "/dinheiro/contabil", element: <AccountingPage />, minRole: "manager" },
    { path: "/dinheiro/plano-de-contas", element: <PlanPage />, minRole: "manager" },
  ],
  widgets: [{ key: "accounting.result", title: "Contábil do mês", minRole: "manager", span: 1, order: 60, element: AccountingWidget }],
});
